import { Reveal } from "@/components/site/Motion";
import { CONTACT_EMAIL } from "@/lib/assets";

const Section = ({ n, title, children }) => (
  <Reveal>
    <section className="mt-10" data-testid={`privacy-section-${n}`}>
      <h2 className="font-display font-bold text-2xl md:text-3xl text-navy">{n}. {title}</h2>
      <div className="font-body text-navy/75 text-lg leading-relaxed mt-3 space-y-3">{children}</div>
    </section>
  </Reveal>
);

const List = ({ items }) => (
  <ul className="space-y-2 mt-1">
    {items.map((it) => (<li key={it} className="flex gap-2"><span className="text-ocean font-bold">→</span> {it}</li>))}
  </ul>
);

const Email = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="font-display font-bold text-ocean hover:underline">{CONTACT_EMAIL}</a>
);

export default function PrivacyPolicy() {
  return (
    <>
      <section className="relative bg-gradient-to-b from-sky/30 to-cream pt-16 md:pt-24 pb-10" data-testid="privacy-hero">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Reveal>
            <p className="font-body text-sm font-bold uppercase tracking-[0.15em] text-ocean mb-4">Privacy</p>
            <h1 className="font-display font-extrabold text-4xl md:text-6xl text-navy leading-[1]">Privacy Policy</h1>
            <p className="font-body text-navy/60 mt-4">Last updated: August 2026</p>
            <p className="font-body text-lg text-navy/75 mt-6">BleuSharks respects the privacy of the people and businesses we work with. This Privacy Policy explains what personal information we may collect, why we use it and how we handle it.</p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24" data-testid="privacy-body">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <Section n={1} title="Who we are">
            <p>BleuSharks provides dedicated professional teams and selected Information Technology project services to businesses.</p>
            <p>For privacy-related questions, contact: <Email />.</p>
          </Section>

          <Section n={2} title="Information we may collect">
            <p>When you contact BleuSharks through our website or communicate with us, we may collect information such as:</p>
            <List items={["Your name", "Business email address", "Phone number", "Company name", "Job title", "Country or location", "Information about your business requirement", "Messages or information you choose to provide"]} />
            <p>If you apply to work with BleuSharks, we may also collect recruitment-related information such as:</p>
            <List items={["Your resume or curriculum vitae", "Contact information", "Employment history", "Education", "Skills and experience", "Interview information", "Video interview or assessment information where applicable", "Other information you choose to provide during recruitment"]} />
            <p>Our website may also collect limited technical information such as browser type, device information, Internet Protocol address and website usage information where applicable.</p>
          </Section>

          <Section n={3} title="How we use information">
            <p>We may use personal information to:</p>
            <List items={["Respond to business enquiries", "Understand service requirements", "Communicate with prospective and existing clients", "Provide and manage our services", "Assess and communicate with candidates", "Manage recruitment and onboarding", "Operate, maintain and improve our website", "Protect our business and website", "Meet legal or regulatory obligations"]} />
          </Section>

          <Section n={4} title="Client systems and data">
            <p>When BleuSharks provides dedicated professionals or Information Technology services, those professionals may need access to client systems or information to perform their assigned work.</p>
            <p>Where BleuSharks processes personal data on behalf of a client, that processing should be carried out according to the client's documented instructions and the applicable service or data processing agreement.</p>
            <p>Access should be limited to the systems and information required for the assigned work.</p>
          </Section>

          <Section n={5} title="International data">
            <p>BleuSharks operates from India and may work with clients and professionals in other countries.</p>
            <p>As a result, personal information may in some situations be accessed or processed across national borders.</p>
            <p>Where applicable, BleuSharks works with clients to put appropriate contractual, organisational and other required safeguards in place for international data processing or transfers.</p>
          </Section>

          <Section n={6} title="How we share information">
            <p>We do not sell personal information.</p>
            <p>Information may be shared where reasonably necessary with:</p>
            <List items={["People within BleuSharks who need the information to perform their work", "Service providers supporting our business operations", "Professional advisers where required", "Authorities where disclosure is legally required", "Clients where appropriate during recruitment, candidate selection or service delivery"]} />
            <p>We aim to limit access to information to people who have a legitimate business need for it.</p>
          </Section>

          <Section n={7} title="Data security">
            <p>BleuSharks uses reasonable technical and organisational measures designed to protect personal information against unauthorised access, loss, misuse, alteration or disclosure.</p>
            <p>Where professionals work with client systems, access should be provided according to the client's own access controls, permissions and security requirements.</p>
            <p>No method of electronic storage or transmission is completely secure, so absolute security cannot be guaranteed.</p>
          </Section>

          <Section n={8} title="Data retention">
            <p>We aim to keep personal information only for as long as it is reasonably needed for the purpose for which it was collected, to maintain appropriate business records, or to meet legal and regulatory requirements.</p>
            <p>Retention periods may vary depending on the type of information and the reason it is held.</p>
          </Section>

          <Section n={9} title="Your privacy rights">
            <p>Depending on where you live, you may have rights relating to your personal information. These may include the right to:</p>
            <List items={["Request access to your personal information", "Ask us to correct inaccurate information", "Request deletion of information in certain circumstances", "Request restriction of certain processing", "Object to certain uses of your information", "Request information about how your data is used", "Exercise other rights available under applicable privacy laws"]} />
            <p>To make a privacy request, contact: <Email />.</p>
            <p>We may need to verify your identity before completing certain requests.</p>
          </Section>

          <Section n={10} title="UK and European privacy rights">
            <p>Where the United Kingdom General Data Protection Regulation, European Union General Data Protection Regulation or other applicable privacy laws apply, BleuSharks aims to handle personal information in accordance with the relevant requirements.</p>
            <p>Where required, the appropriate legal basis for processing and appropriate safeguards for international data transfers should be established.</p>
            <p>Individuals may also have the right to raise a concern with the relevant data protection authority in their country.</p>
          </Section>

          <Section n={11} title="Third-party services and links">
            <p>Our website may contain links to third-party websites, tools or services.</p>
            <p>BleuSharks is not responsible for the privacy practices of independent third parties. We encourage users to review the privacy information provided by those services.</p>
          </Section>

          <Section n={12} title="Changes to this policy">
            <p>We may update this Privacy Policy when our services, website or legal requirements change.</p>
            <p>The latest version will be published on this page with the updated date.</p>
          </Section>

          <Section n={13} title="Contact">
            <p>For questions about this Privacy Policy or how BleuSharks handles personal information, contact: <Email />.</p>
          </Section>
        </div>
      </section>
    </>
  );
}
